import { Link } from 'gatsby';
import React, { useState } from 'react';
import ResourceTile from './ResourceTile';

interface ResourceGridProps {
  title?: string;
  lead?: string;
  viewAllUrl?: string;
  resources: {
    title: string;
    url: string;
    thumbnailUrl: string;
    excerpt?: string;
    date?: string;
    aspectHeight?: string;
    aspectWidth?: string;
    type:
      | 'press-release'
      | 'blog-post'
      | 'customer-story-brand'
      | 'customer-story-retail'
      | 'report'
      | 'case-study'
      | 'video';
  }[];
  cols?: number;
  overrideColor?: string;
  fixedWidth?: boolean;
  noIcon?: boolean;
  customerStoriesFullLabel?: boolean;
  noTag?: boolean;
  videoIconInverted?: boolean;
}

const ResourceGrid: React.FC<ResourceGridProps> = ({
  title,
  lead,
  viewAllUrl,
  resources,
  cols = 3,
  overrideColor,
  fixedWidth,
  noIcon,
  customerStoriesFullLabel,
  noTag,
  videoIconInverted,
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const gridColsMapping = {
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  };

  return (
    <div
      className={`container max-w-screen-xl my-20 md:my-32 mx-auto ${
        fixedWidth ? '' : 'blog-width'
      }`}
    >
      {lead && (
        <div className="text-center mb-3 text-mid-grey opacity-60 text-lg leading-8 tracking-tighter font-semibold">
          {lead}
        </div>
      )}
      {title && (
        <h2
          className={`mb-10 text-center md:text-left flex flex-col md:flex-row items-center justify-between u-h2 ${
            fixedWidth ? 'px-6 md:px-0' : ''
          } ${
            lead ? 'md:px-16 lg:px-48 text-center md:text-center md:mb-20' : ''
          }`}
        >
          {title}
          {viewAllUrl && (
            <Link
              className="u-cta-link md:u-btn md:u-btn--outline md:rounded-full md:border mt-4 md:mt-0"
              to={viewAllUrl}
            >
              See all <span className="inline md:hidden">{title}</span>
            </Link>
          )}
        </h2>
      )}

      <div
        className={
          fixedWidth ? 'overflow-x-auto px-6 md:px-0 scrollbars-hidden' : ''
        }
      >
        <div
          className={
            fixedWidth
              ? 'md:flex md:flex-nowrap md:space-x-8 space-y-8 md:space-y-0'
              : `grid grid-cols-1 gap-8 ${gridColsMapping[cols]} md:gap-6`
          }
        >
          {resources.map((resource, index) => (
            <div
              key={index}
              className={`press-tile-wrapper mb-8 ${
                fixedWidth ? 'w-full md:w-96 lg:w-120 flex-shrink-0' : ''
              } transition-opacity duration-200 ease-in-out ${
                hoverIndex !== null && index !== hoverIndex
                  ? 'opacity-50'
                  : 'opacity-100'
              }`}
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <ResourceTile
                aspectHeight={resource.aspectHeight}
                aspectWidth={resource.aspectWidth}
                title={resource.title}
                image={resource.thumbnailUrl}
                type={resource.type}
                overrideColor={overrideColor}
                url={resource.url}
                excerpt={resource.excerpt}
                noIcon={noIcon}
                noHover
                customerStoriesFullLabel={customerStoriesFullLabel}
                noTag={noTag}
                date={resource.date}
                videoIconInverted={videoIconInverted}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResourceGrid;
